import { useId } from "react";
import type { ReportDesignerReview } from "@iamu/shared";
import { Kicker } from "@/components/ui/kicker";
import { MarkdownProse } from "@/components/ui/markdown-prose";
import { cn } from "@/lib/utils";

/**
 * The designer's review of a run: a second eye that judged the screens
 * the persona saw against the project's design docs. Renders the structured
 * summary, broken design rules with source file citations, and design causes
 * that explain the persona's findings (without repeating per-screen finding
 * bullets that already appear in the findings list above).
 */
export function DesignerReviewSection({
  review,
  hideDesignCauses,
  className,
}: {
  review: ReportDesignerReview;
  hideDesignCauses?: boolean;
  className?: string;
}) {
  const headingId = useId();
  const rulesBroken = review.rulesBroken ?? [];
  const hasStructuredContent =
    Boolean(review.summary) || rulesBroken.length > 0;

  return (
    <section className={cn("space-y-4", className)} aria-labelledby={headingId}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <Kicker as="h2" className="text-foreground">
            <span id={headingId}>Designer's review</span>
          </Kicker>
          {review.label && (
            <p className="text-xs text-muted-foreground">{review.label}</p>
          )}
        </div>
        {review.rulesTally && (
          <span
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium tabular-nums",
              review.rulesTally.broken > 0
                ? "border-destructive/30 bg-destructive/10 text-destructive"
                : "border-border bg-muted text-muted-foreground",
            )}
          >
            <span>
              {review.rulesTally.broken}{" "}
              {review.rulesTally.broken === 1 ? "rule" : "rules"} broken
            </span>
            <span aria-hidden="true">·</span>
            <span className="text-muted-foreground">
              {review.rulesTally.held} held
            </span>
          </span>
        )}
      </div>

      {review.summary ? (
        <p className="text-sm text-foreground leading-relaxed">
          {review.summary}
        </p>
      ) : (
        !hasStructuredContent &&
        review.markdown && (
          <MarkdownProse content={review.markdown} className="text-sm" />
        )
      )}

      {rulesBroken.length > 0 && (
        <div className="space-y-2.5 border-t border-border/60 pt-3.5">
          <Kicker as="h3">Rules broken ({rulesBroken.length})</Kicker>
          <ul className="divide-y divide-border/60">
            {rulesBroken.map((r) => (
              <li
                key={`${r.rule}-${r.file ?? ""}-${r.where ?? ""}`}
                className="py-2.5 first:pt-1 last:pb-0"
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm">
                  <span className="font-medium text-foreground">
                    “{r.rule}”
                  </span>
                  {r.file && (
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
                      {r.file}
                    </span>
                  )}
                  {r.where && (
                    <span className="text-xs text-muted-foreground">
                      · {r.where}
                    </span>
                  )}
                </div>
                {r.detail && (
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                    {r.detail}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!hideDesignCauses && review.designCauses.length > 0 && (
        <div className="space-y-2.5 border-t border-border/60 pt-3.5">
          <Kicker as="h3">Design causes of the persona's findings</Kicker>
          <ul className="divide-y divide-border/60">
            {review.designCauses.map((c) => (
              <li
                key={`${c.finding}-${c.markdown}`}
                className="py-2.5 first:pt-1 last:pb-0"
              >
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span>
                    Explains{" "}
                    <span className="font-medium text-foreground">
                      “{c.finding}”
                    </span>
                  </span>
                  {c.code && <span className="font-mono">· {c.code}</span>}
                  {c.breaksRule && <span>· Breaks “{c.breaksRule}”</span>}
                </div>
                {c.detail ? (
                  <p className="mt-1 text-sm text-foreground leading-relaxed">
                    {c.detail}
                  </p>
                ) : (
                  <MarkdownProse
                    content={c.markdown}
                    className="mt-1 text-sm prose-p:my-1"
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
